import { ErrorHandler, inject, Injectable } from '@angular/core';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';

/**
 * Detects Angular router `NoMatch` sentinel errors.
 *
 * The router's `Recognizer.expandSegmentAgainstRouteUsingRedirect` throws
 * `NoMatch` as part of normal control flow — it tries redirect targets, throws
 * on each miss, catches, tries the next. From Angular 17+ these intermediate
 * throws can leak to `unhandledrejection` even when the router eventually
 * resolves the URL successfully. The error has no `message`, just a
 * `segmentGroup` field, so the standard pattern-matching on `.message` doesn't
 * catch it. See AM-4393.
 */
function isRouterNoMatch(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }
  // `NoMatch` carries a `UrlSegmentGroup` on `segmentGroup` (an object with a
  // `segments` array) and has no `message`. Detect it by this shape rather than
  // by `constructor.name`: production builds mangle the `NoMatch` /
  // `UrlSegmentGroup` class names (e.g. to `k` / `vs`), so name-based checks
  // match only in dev and let every sentinel through to Datadog in prod.
  // Property names survive minification, so the shape is the reliable signal.
  const segmentGroup = (err as { segmentGroup?: { segments?: unknown } }).segmentGroup;
  return segmentGroup != null && typeof segmentGroup === 'object' && Array.isArray(segmentGroup.segments);
}

/**
 * Detects webpack `ChunkLoadError` — thrown when a lazy-loaded chunk cannot
 * be fetched, typically because the user has a stale cached bundle after a
 * new deployment. The `main.ts` unhandledrejection handler already triggers a
 * page reload to recover; logging these to Datadog is noise. See AM-4414.
 */
function isChunkLoadError(err: unknown): boolean {
  return err instanceof Error && err.name === 'ChunkLoadError';
}

/**
 * Detects an Angular router navigation that was superseded by a later one.
 *
 * From Angular 20+ the router recognizes a URL under an `AbortSignal` and
 * aborts it when a new navigation starts, so the abandoned navigation's promise
 * rejects with `AbortError: signal is aborted without reason` — reaching
 * `unhandledrejection` even though the router then completes the navigation the
 * user actually asked for. Nothing is broken: the later navigation wins, which
 * is the point.
 *
 * It surfaced with AM-4717's bottom tab bar, which puts five lazy-loaded
 * destinations one tap apart — tapping a second one while the first is still
 * fetching its chunk is now an ordinary thing to do, not an edge case.
 */
function isSupersededNavigation(err: unknown): boolean {
  // Matched on the message, not `name`: the rejection is a plain Error whose
  // message embeds the DOMException text, so `err.name` is 'Error'. Keep the
  // pattern narrow so a genuine aborted fetch elsewhere still gets reported.
  return err instanceof Error && /AbortError: signal is aborted/.test(err.message);
}

@Injectable({
  providedIn: 'root',
})
export class SharedErrorService implements ErrorHandler {
  private notify = inject(SharedNotificationService);
  private log = inject(SharedLoggingService);

  private static listenersAdded = false;

  constructor() {
    if (!SharedErrorService.listenersAdded) {
      SharedErrorService.listenersAdded = true;
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        if (isRouterNoMatch(event.reason) || isChunkLoadError(event.reason) || isSupersededNavigation(event.reason)) {
          event.preventDefault();
          return;
        }
        this.handleError(event.reason);
      });
      window.addEventListener('rejectionhandled', (event: PromiseRejectionEvent) => {
        if (isRouterNoMatch(event.reason) || isSupersededNavigation(event.reason)) {
          return;
        }
        this.handleError(event.reason);
      });
    }
  }

  public handleError(err: unknown, notificationMessage?: string, notificationTitle?: string): void {
    if (isRouterNoMatch(err) || isSupersededNavigation(err)) {
      return;
    }

    void this.log.error(err);

    if (notificationMessage) {
      this.notify.error(notificationMessage, notificationTitle);
    }
  }
}
